import axios from 'axios';
import { normalizeContactData } from '../utils/contactDataUtils';
import { notifyUnauthorized } from './authEvents';

const RETRY_ATTEMPTS = 3;
const RETRY_DELAY_MS = 1000;

const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || '',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  paramsSerializer: {
    indexes: null, // Repeat format (filter=a&filter=b), not indexed
  }
});

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { config, response } = error;
    const retryCount = config?.__retryCount || 0;

    // The session ended or never started: back to the sign-in page
    if (response?.status === 401) {
      notifyUnauthorized();
      return Promise.reject(error);
    }

    // Retry reads that failed on the network or the server; client errors (4xx) are final
    if (config && config.method === 'get' && (!response || response.status >= 500) && retryCount < RETRY_ATTEMPTS) {
      config.__retryCount = retryCount + 1;
      await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS));
      return apiClient(config);
    }

    console.error(`API error: ${config?.method?.toUpperCase()} ${config?.url}`, response?.status || error.message);
    return Promise.reject(error);
  }
);

// Drops empty values and joins id lists, which the API takes comma-separated
const cleanParams = (params = {}) => {
  const cleaned = {};
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    if (Array.isArray(value) && (key === 'company_ids' || key === 'position_ids')) {
      if (value.length > 0) cleaned[key] = value.join(',');
      return;
    }
    cleaned[key] = value;
  });
  return cleaned;
};

// Error carrying the API's `detail` message when there is one
const toError = (error, fallbackMessage) => {
  const detail = error.response?.data?.detail;
  const apiError = new Error(typeof detail === 'string' ? detail : (error.message || fallbackMessage));
  apiError.status = error.response?.status;
  apiError.data = error.response?.data;
  apiError.originalError = error;
  return apiError;
};

const normalizePagination = (pagination, count) => {
  const total = pagination?.total_items ?? pagination?.total_count ?? count;
  return {
    page: pagination?.page || 1,
    limit: pagination?.limit || count,
    total_items: total,
    total_count: total,
    total_pages: pagination?.total_pages || 1,
    has_next: Boolean(pagination?.has_next),
    has_prev: Boolean(pagination?.has_previous ?? pagination?.has_prev)
  };
};

class ContactApiService {

  /**
   * Advisors, i.e. the people whose LinkedIn exports were imported
   * @returns {Promise<Array<{name: string, contact_count: number, percentage: number}>>}
   */
  async getSources() {
    try {
      const { data } = await apiClient.get('/api/v2/contacts/sources');
      return (data?.sources || []).map(source => ({
        name: source.source,
        contact_count: source.contact_count,
        percentage: source.percentage
      }));
    } catch (error) {
      throw toError(error, 'Failed to fetch advisors');
    }
  }

  /**
   * Industries with company and people counts
   * @param {Object} params - Query options (limit, sort, order, ...)
   * @returns {Promise<Array>} Industries
   */
  async getIndustries(params = {}) {
    try {
      const { data } = await apiClient.get('/api/v2/search/industries', { params: cleanParams(params) });
      return (data?.data || []).map(industry => ({
        id: industry.name,
        name: industry.name,
        company_count: industry.company_count || 0,
        contact_count: industry.contact_count || 0,
        description: industry.description || null
      }));
    } catch (error) {
      throw toError(error, 'Failed to fetch industries');
    }
  }

  /**
   * Industry → company overview with people counts
   * @param {Object} params - company_filter, contact_filter, position_filter, industry_filter,
   *   advisor_filter, q, max_companies (companies listed per industry), limit, page
   * @returns {Promise<Object>} The /api/v2/search/industry-company response
   */
  async searchIndustryCompanyPeople(params = {}) {
    try {
      const { data } = await apiClient.get('/api/v2/search/industry-company', { params: cleanParams(params) });
      return data;
    } catch (error) {
      throw toError(error, 'Failed to load the industry overview');
    }
  }

  /**
   * People matching the filters, normalised for display
   * @param {Object} params - The overview filters plus company_ids, position_ids, dedupe,
   *   page, limit, sort and order
   * @returns {Promise<{results: Array, pagination: Object}>}
   */
  async searchContacts(params = {}) {
    try {
      const { data } = await apiClient.get('/api/v2/search/contacts', { params: cleanParams(params) });
      const results = (data?.data || []).map(normalizeContactData);
      return { results, pagination: normalizePagination(data?.pagination, results.length) };
    } catch (error) {
      throw toError(error, 'Failed to search contacts');
    }
  }

  /**
   * Companies with people counts
   * @param {Object} params - q, filter (field:operator:value, repeatable), page, limit, sort, order
   * @returns {Promise<{companies: Array, pagination: Object}>}
   */
  async searchCompanies(params = {}) {
    try {
      const { data } = await apiClient.get('/api/v2/search/companies', { params: cleanParams(params) });
      const companies = data?.data || [];
      return { companies, pagination: normalizePagination(data?.pagination, companies.length) };
    } catch (error) {
      throw toError(error, 'Failed to search companies');
    }
  }

  /**
   * Companies ranked by how many people match the filters (most first), or with
   * group_by 'icp' the target accounts in ICP and list order
   * @param {Object} params - The overview filters plus page, limit, group_by and icp_filter
   * @returns {Promise<{companies: Array, pagination: Object}>}
   */
  async searchCompaniesByPeople(params = {}) {
    try {
      const { data } = await apiClient.get('/api/v2/search/company-people', {
        params: cleanParams({ max_people: 1, ...params })
      });
      const companies = (data?.data || []).map(company => ({
        id: company.id,
        name: company.name,
        company_name: company.metadata?.company_name || company.name,
        contact_count: company.metadata?.contact_count || 0,
        network_people: company.metadata?.network_people || 0,
        target_account: company.metadata?.target_account || null,
        industry: company.metadata?.industry,
        logo_uuid: company.metadata?.logo_uuid,
        logo_url: company.metadata?.logo_url
      }));
      return { companies, pagination: normalizePagination(data?.pagination, companies.length) };
    } catch (error) {
      throw toError(error, 'Failed to load companies');
    }
  }

  /**
   * People at one company by seniority, job title and advisor
   * @param {number} companyId - Company ID
   * @returns {Promise<Object>} people_count, seniority, positions, advisors, shared_people
   */
  async getCompanyAnalytics(companyId) {
    try {
      const { data } = await apiClient.get(`/api/v2/companies/${companyId}/positions`);
      return data;
    } catch (error) {
      throw toError(error, 'Failed to load company details');
    }
  }

  /**
   * Every target account across every ICP list, for the pipeline view. Not paginated.
   * @returns {Promise<{accounts: Array, count: number}>}
   */
  async searchTargetAccounts() {
    try {
      const { data } = await apiClient.get('/api/v2/search/target-accounts');
      return data;
    } catch (error) {
      throw toError(error, 'Failed to load the pipeline');
    }
  }

  /**
   * Move a target account on the Kanban board: a new outreach_stage, a new kanban_position
   * (a midpoint between its new neighbors), or both when a card is dragged into a new column.
   * @param {number} accountId
   * @param {{outreach_stage?: string, kanban_position?: number}} changes
   */
  async moveTargetAccount(accountId, changes) {
    try {
      const { data } = await apiClient.patch(`/api/v2/target-accounts/${accountId}`, changes);
      return data;
    } catch (error) {
      throw toError(error, 'Failed to move the account');
    }
  }

  /**
   * Add an existing company to the pipeline, with existing contacts there as who to reach.
   * @param {{company_id: number, icp_id: number, tier?: string, contacts?: {contact_id: number, role: string}[]}} body
   * @returns {Promise<Object>} The new row, same shape as a searchTargetAccounts() row
   */
  async createTargetAccount(body) {
    try {
      const { data } = await apiClient.post('/api/v2/target-accounts', body);
      return data;
    } catch (error) {
      throw toError(error, 'Failed to add the account');
    }
  }

  /** Every contact on file at one company, for picking who to reach when adding it to the pipeline. */
  async getCompanyContacts(companyId) {
    try {
      const { data } = await apiClient.get('/api/v2/search/company-contacts', { params: { company_id: companyId } });
      return data;
    } catch (error) {
      throw toError(error, 'Failed to load contacts at this company');
    }
  }

  /**
   * One-call snapshot for the Dashboard view: totals, recent real LinkedIn imports, recent audit activity.
   * @param {Object} params - days, jobs_limit, events_limit (all optional)
   * @returns {Promise<Object>} The /api/v2/dashboard response
   */
  async getDashboard(params = {}) {
    try {
      const { data } = await apiClient.get('/api/v2/dashboard', { params: cleanParams(params) });
      return data;
    } catch (error) {
      throw toError(error, 'Failed to load the dashboard');
    }
  }

  /**
   * Contacts with LinkedIn-profile-PDF background on file (education, work history, bio,
   * headline, summary), for the Icebreakers view.
   * @returns {Promise<{contacts: Array}>}
   */
  async getContactsWithBackground() {
    try {
      const { data } = await apiClient.get('/api/v2/search/contacts-with-background');
      return data;
    } catch (error) {
      throw toError(error, 'Failed to load background info');
    }
  }

  /**
   * One person, including every advisor who knows them (known_by)
   * @param {number} contactId - Contact ID
   * @returns {Promise<Object>} Normalised contact
   */
  async getContact(contactId) {
    try {
      const { data } = await apiClient.get(`/api/v2/contacts/${contactId}`);
      return normalizeContactData(data);
    } catch (error) {
      throw toError(error, `Failed to fetch contact ${contactId}`);
    }
  }

  /**
   * Re-read a contact, e.g. after it was edited
   * @param {number} contactId - Contact ID
   * @returns {Promise<Object>} Normalised contact
   */
  async refreshContact(contactId) {
    return this.getContact(contactId);
  }

  /**
   * Update a person's email_address, phone, or city + country. The change applies to every
   * advisor's record of the person; empty strings clear a field.
   * @param {number} contactId - Contact ID
   * @param {Object} changes - Fields to change
   * @returns {Promise<Object>} The updated, normalised contact
   */
  async updateContact(contactId, changes) {
    try {
      const { data } = await apiClient.patch(`/api/v2/contacts/${contactId}`, changes);
      return normalizeContactData(data);
    } catch (error) {
      throw toError(error, 'Failed to update contact');
    }
  }

  /**
   * Known city/country pairs whose city starts with `query`
   * @param {string} query - Start of a city name
   * @param {number} limit - Maximum suggestions
   * @returns {Promise<Array<{city: string, country: string, display_name: string}>>}
   */
  async searchLocations(query, limit = 10) {
    try {
      const { data } = await apiClient.get('/api/v2/locations/search', { params: { query, limit } });
      return Array.isArray(data) ? data : [];
    } catch (error) {
      throw toError(error, 'Failed to search locations');
    }
  }

  /**
   * The relationship-manager master list, by name, with how many companies each one manages
   * @param {boolean} includeInactive - Include managers marked inactive
   * @returns {Promise<Array<Object>>} id, name, title, email, phone, aliases, notes, active, company_count
   */
  async getRelationshipManagers(includeInactive = true) {
    try {
      const { data } = await apiClient.get('/api/v2/relationship-managers', {
        params: { include_inactive: includeInactive }
      });
      return data.managers || [];
    } catch (error) {
      throw toError(error, 'Failed to load relationship managers');
    }
  }

  /**
   * One relationship manager with the companies they manage
   * @param {number} managerId - Manager ID
   * @returns {Promise<Object>} The manager, plus companies: [{id, name, on_target_list, source}]
   */
  async getRelationshipManager(managerId) {
    try {
      const { data } = await apiClient.get(`/api/v2/relationship-managers/${managerId}`);
      return data;
    } catch (error) {
      throw toError(error, 'Failed to load the relationship manager');
    }
  }

  /**
   * Add someone to the master list
   * @param {Object} fields - name (required), title, email, phone, aliases, notes
   * @returns {Promise<Object>} The new manager
   */
  async createRelationshipManager(fields) {
    try {
      const { data } = await apiClient.post('/api/v2/relationship-managers', fields);
      return data;
    } catch (error) {
      throw toError(error, 'Failed to add the relationship manager');
    }
  }

  /**
   * Change a manager's details; `active: false` marks them inactive. Empty strings clear a field.
   * @param {number} managerId - Manager ID
   * @param {Object} changes - Fields to change
   * @returns {Promise<Object>} The updated manager
   */
  async updateRelationshipManager(managerId, changes) {
    try {
      const { data } = await apiClient.patch(`/api/v2/relationship-managers/${managerId}`, changes);
      return data;
    } catch (error) {
      throw toError(error, 'Failed to update the relationship manager');
    }
  }

  /**
   * Remove someone who manages no companies from the master list
   * @param {number} managerId - Manager ID
   */
  async deleteRelationshipManager(managerId) {
    try {
      await apiClient.delete(`/api/v2/relationship-managers/${managerId}`);
    } catch (error) {
      throw toError(error, 'Failed to delete the relationship manager');
    }
  }

  /**
   * Set who manages the relationship with a company
   * @param {number} companyId - Company ID
   * @param {number[]} managerIds - Every manager of the company (empty for nobody)
   * @returns {Promise<Array<Object>>} The company's managers now
   */
  async setCompanyRelationshipManagers(companyId, managerIds) {
    try {
      const { data } = await apiClient.put(`/api/v2/companies/${companyId}/relationship-managers`, {
        manager_ids: managerIds
      });
      return data.relationship_managers || [];
    } catch (error) {
      throw toError(error, 'Failed to save the relationship managers');
    }
  }
}

const contactApi = new ContactApiService();

export default contactApi;
